import React from "react";

/**
 * Brand logos using pure SVG implementations for maximum stability and visual quality.
 * Eliminates external dependencies and loading states that cause visual glitches.
 */

// Common prop types
interface LogoProps {
  className?: string;
  size?: "small" | "default" | "large";
  useWhite?: boolean;
}

const getSizeClass = (size: string) => {
  switch (size) {
    case "small": return "h-8";
    case "large": return "h-16 md:h-24";
    default: return "h-12 md:h-16";
  }
};

/**
 * STEK Logo - Geometric, Bold, Monospace-ish
 */
export function STEKLogo({ className = "", size = "default", useWhite = false }: LogoProps) {
  const sizeClass = getSizeClass(size);
  const color = useWhite ? "white" : "currentColor";

  return (
    <div className={`${sizeClass} flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 200 60"
        className="w-full h-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <title>STEK</title>
        <path
          d="M20 45C10 45 5 40 5 30H15C15 35 18 38 20 38C22 38 25 35 25 32C25 25 5 25 5 12C5 5 10 0 20 0C30 0 35 5 35 12H25C25 8 22 7 20 7C18 7 15 9 15 11C15 17 35 17 35 32C35 40 30 45 20 45Z"
          fill={color}
        />
        <path
          d="M45 0H85V7H70V45H60V7H45V0Z"
          fill={color}
        />
        <path
          d="M95 0H135V7H105V20H130V27H105V38H135V45H95V0Z"
          fill={color}
        />
        <path
          d="M145 0H155V20L175 0H188L165 22L190 45H178L155 24V45H145V0Z"
          fill={color}
        />
      </svg>
    </div>
  );
}

/**
 * Koch Chemie Logo - Stylized Serif Text or KC Monogram
 * Using the distinct Serif text style
 */
export function KochChemieLogo({ className = "", size = "default", useWhite = false }: LogoProps) {
  const sizeClass = getSizeClass(size);
  const color = useWhite ? "white" : "currentColor";

  return (
    <div className={`${sizeClass} flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 300 50"
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
         <title>Koch Chemie</title>
         <text 
            x="50%" 
            y="50%" 
            dominantBaseline="middle" 
            textAnchor="middle" 
            fontFamily="Times New Roman, serif" 
            fontWeight="bold" 
            fontSize="42" 
            fill={color}
            letterSpacing="2"
         >
            KOCH CHEMIE
         </text>
         {/* Underline/Accent */}
         <rect x="20" y="45" width="260" height="3" fill={useWhite ? "white" : "#E31E24"} rx="1" />
      </svg>
    </div>
  );
}

/**
 * Tonyin Logo - Stylized Italic Bold
 */
export function TonyinLogo({ className = "", size = "default", useWhite = false }: LogoProps) {
  const sizeClass = getSizeClass(size);
  const color = useWhite ? "white" : "currentColor";

  return (
    <div className={`${sizeClass} flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 200 60"
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <title>Tonyin</title>
        <g transform="skewX(-10)">
            <text 
                x="50%" 
                y="50%" 
                dominantBaseline="middle" 
                textAnchor="middle" 
                fontFamily="Arial, sans-serif" 
                fontWeight="900" 
                fontSize="48" 
                fill={color}
                letterSpacing="1"
            >
                Tonyin
            </text>
            <rect x="25" y="48" width="150" height="4" fill={useWhite ? "white" : "#FFD700"} rx="2" />
        </g>
      </svg>
    </div>
  );
}

/**
 * Brand logo component factory
 */
export default function BrandLogo({
  brand,
  className = "",
  size = "default",
  useWhite = false,
}: { brand: string } & LogoProps) {
  const logos: Record<string, React.FC<LogoProps>> = {
    stek: STEKLogo,
    "koch-chemie": KochChemieLogo,
    tonyin: TonyinLogo,
  };

  const LogoComponent = logos[brand];

  if (!LogoComponent) {
    return (
      <div className={`text-xl font-bold ${className} uppercase`}>
        {brand}
      </div>
    );
  }

  return (
    <LogoComponent className={className} size={size} useWhite={useWhite} />
  );
}
